class ErrorTrackerService {
    constructor() {
        this.errors = new Map();
        this.errorCounts = {}; // timestamp -> count for trends
        this.TREND_WINDOW = 5 * 60 * 1000; // 5 minutes
        this.sessionId = this.generateSessionId();
        this.startTime = Date.now();
        this.setupPeriodicCleanup();
    }
    generateSessionId() {
        return `tracker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    setupPeriodicCleanup() {
        // Clean up old errors every hour
        setInterval(() => {
            this.cleanupOldErrors();
            this.cleanupOldTrends();
        }, 60 * 60 * 1000);
    }
    cleanupOldErrors() {
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        for (const [id, error] of this.errors.entries()) {
            if (error.lastOccurred < oneDayAgo && error.resolved) {
                this.errors.delete(id);
            }
        }
    }
    cleanupOldTrends() {
        const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;
        for (const timestamp in this.errorCounts) {
            if (parseInt(timestamp) < sixHoursAgo) {
                delete this.errorCounts[timestamp];
            }
        }
    }
    generateErrorKey(error, context) {
        // Create a unique key based on error type, message, and context
        const contextKey = `${context.component || 'unknown'}_${context.url}`;
        return `${error.name}_${error.message}_${contextKey}`;
    }
    trackError(error, level = 'error', context = {}) {
        const fullContext = {
            sessionId: this.sessionId,
            userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
            url: typeof window !== 'undefined' ? window.location.href : 'unknown',
            timestamp: Date.now(),
            ...context
        };
        const errorKey = this.generateErrorKey(error, fullContext);
        const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // Update trend data
        const trendKey = Math.floor(Date.now() / this.TREND_WINDOW) * this.TREND_WINDOW;
        this.errorCounts[trendKey] = (this.errorCounts[trendKey] || 0) + 1;
        if (this.errors.has(errorKey)) {
            // Update existing error
            const existingError = this.errors.get(errorKey);
            existingError.occurrenceCount += 1;
            existingError.lastOccurred = Date.now();
            existingError.resolved = false; // Mark as unresolved again
        }
        else {
            // Create new error entry
            const trackedError = {
                id: errorId,
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                },
                level,
                context: fullContext,
                resolved: false,
                occurrenceCount: 1,
                firstOccurred: Date.now(),
                lastOccurred: Date.now()
            };
            this.errors.set(errorKey, trackedError);
        }
        return errorId;
    }
    markErrorAsResolved(errorId) {
        for (const [key, error] of this.errors.entries()) {
            if (error.id === errorId) {
                error.resolved = true;
                error.resolvedAt = Date.now();
                return true;
            }
        }
        return false;
    }
    getErrorMetrics() {
        const errors = Array.from(this.errors.values());
        const now = Date.now();
        const sessionDuration = (now - this.startTime) / 1000 / 60; // minutes
        // Calculate errors by level
        const errorsByLevel = errors.reduce((acc, error) => {
            acc[error.level] = (acc[error.level] || 0) + error.occurrenceCount;
            return acc;
        }, { error: 0, warning: 0, info: 0 });
        // Calculate errors by type
        const errorsByType = errors.reduce((acc, error) => {
            acc[error.error.name] = (acc[error.error.name] || 0) + error.occurrenceCount;
            return acc;
        }, {});
        // Calculate errors by component
        const errorsByComponent = errors.reduce((acc, error) => {
            const component = error.context.component || 'unknown';
            acc[component] = (acc[component] || 0) + error.occurrenceCount;
            return acc;
        }, {});
        // Calculate errors by page
        const errorsByPage = errors.reduce((acc, error) => {
            const url = new URL(error.context.url || 'http://localhost').pathname;
            acc[url] = (acc[url] || 0) + error.occurrenceCount;
            return acc;
        }, {});
        // Calculate total errors
        const totalErrors = errors.reduce((sum, error) => sum + error.occurrenceCount, 0);
        // Calculate error rate (errors per minute)
        const errorRate = sessionDuration > 0 ? totalErrors / sessionDuration : 0;
        // Find most frequent errors
        const mostFrequentErrors = errors
            .map(error => ({
            message: `${error.error.name}: ${error.error.message}`,
            count: error.occurrenceCount,
            lastOccurred: error.lastOccurred
        }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
        // Generate error trends
        const errorTrends = Object.entries(this.errorCounts)
            .map(([timestamp, count]) => ({ timestamp: parseInt(timestamp), count }))
            .sort((a, b) => a.timestamp - b.timestamp);
        return {
            totalErrors,
            errorsByLevel,
            errorsByType,
            errorsByComponent,
            errorsByPage,
            errorRate,
            averageErrorsPerSession: totalErrors, // For single session
            mostFrequentErrors,
            errorTrends
        };
    }
    getUnresolvedErrors() {
        return Array.from(this.errors.values()).filter(error => !error.resolved);
    }
    getRecentErrors(minutes = 30) {
        const cutoff = Date.now() - minutes * 60 * 1000;
        return Array.from(this.errors.values())
            .filter(error => error.lastOccurred >= cutoff)
            .sort((a, b) => b.lastOccurred - a.lastOccurred);
    }
    getErrorsByComponent(component) {
        return Array.from(this.errors.values())
            .filter(error => error.context.component === component)
            .sort((a, b) => b.lastOccurred - a.lastOccurred);
    }
    getErrorTrend(hours = 1) {
        const cutoff = Date.now() - hours * 60 * 60 * 1000;
        return Object.entries(this.errorCounts)
            .filter(([timestamp]) => parseInt(timestamp) >= cutoff)
            .map(([timestamp, count]) => ({ timestamp: parseInt(timestamp), count }))
            .sort((a, b) => a.timestamp - b.timestamp);
    }
    exportErrorData(format = 'json') {
        const errors = Array.from(this.errors.values());
        if (format === 'csv') {
            const headers = [
                'ID', 'Error Name', 'Message', 'Level', 'Component', 'URL',
                'Occurrences', 'First Occurred', 'Last Occurred', 'Resolved'
            ];
            const rows = errors.map(error => [
                error.id,
                error.error.name,
                error.error.message,
                error.level,
                error.context.component || '',
                error.context.url,
                error.occurrenceCount,
                new Date(error.firstOccurred).toISOString(),
                new Date(error.lastOccurred).toISOString(),
                error.resolved ? 'Yes' : 'No'
            ]);
            return [headers, ...rows].map(row => row.join(',')).join('\n');
        }
        return JSON.stringify(errors, null, 2);
    }
    clearErrorData() {
        this.errors.clear();
        this.errorCounts = {};
    }
    // Health check method
    getHealthStatus() {
        const metrics = this.getErrorMetrics();
        const unresolvedErrors = this.getUnresolvedErrors().length;
        const recentErrors = this.getRecentErrors(10).length; // Last 10 minutes
        let status = 'healthy';
        if (metrics.errorRate > 10 || unresolvedErrors > 20) {
            status = 'critical';
        }
        else if (metrics.errorRate > 5 || unresolvedErrors > 10 || recentErrors > 5) {
            status = 'warning';
        }
        return {
            status,
            errorRate: metrics.errorRate,
            unresolvedErrors,
            recentErrors
        };
    }
}
// Global instance
export const errorTracker = new ErrorTrackerService();
// React hook for using error tracker
export function useErrorTracker() {
    const trackError = (error, level, context) => {
        return errorTracker.trackError(error, level, context);
    };
    const getMetrics = () => errorTracker.getErrorMetrics();
    const getUnresolvedErrors = () => errorTracker.getUnresolvedErrors();
    const markResolved = (errorId) => errorTracker.markErrorAsResolved(errorId);
    return {
        trackError,
        getMetrics,
        getUnresolvedErrors,
        markResolved
    };
}
//# sourceMappingURL=error-tracker.js.map